import { isFulfilled } from "@reduxjs/toolkit";
import { format, isValid } from "date-fns";
import { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useAppDispatch } from "../../../../app/hooks";
import { getFormattedDate, isoFormat } from "../../../../common/helpers/formatDateTime";
import { NameIdOption } from "../../../../common/types/commonTypes";
import { AccumulationCalculationMultiple } from "../../../employment/employmentApi";
import { fetchSalaries } from "../../settingsActions";
import { Kta } from "../../types/queries";
import { BULK_SALARY_INCREASE_FILTER_NAMES, BULK_SALARY_INCREASE_TARGETS } from "../constants";
import { formatToQuery, resolveKtaQueryEndDate } from "../helpers";
import { BulkSalaryIncreaseFilterValues } from "../types";
import useBulkSalaryData from "../useBulkSalaryData";
import KtaIncreaseConfigRows from "./KtaIncreaseConfigRows";
import SalaryIncreaseConfigBasic from "./SalaryIncreaseConfigBasic";

type Props = {
    handleExpands: (salaryComponent: string) => void;
    addExpands: (salaryComponent: string, open: boolean) => void;
    expands: Record<string, boolean>;
};
const KtaIncreaseConfig = ({
                               handleExpands,
                               addExpands,
                               expands,
                           }: Props) => {
    const dispatch = useAppDispatch();

    const {control, watch, setValue, getValues} =
        useFormContext<BulkSalaryIncreaseFilterValues>();

    const {fetchAccumulations} = useBulkSalaryData({filterValues: getValues()});
    const tes = watch(
        BULK_SALARY_INCREASE_FILTER_NAMES.TES,
    ) as NameIdOption | null;

    const salaryIncreaseStartDate = watch(
        BULK_SALARY_INCREASE_FILTER_NAMES.SALARY_INCREASE_START_DATE,
    );

    useEffect(() => {
        if (
            tes?.id &&
            salaryIncreaseStartDate &&
            isValid(salaryIncreaseStartDate)
        ) {
            const filters = getValues();
            const query = formatToQuery({...filters, target: BULK_SALARY_INCREASE_TARGETS.KTA_SINGLE});
            dispatch(fetchSalaries(query)).then((action) => {
                if (isFulfilled(action)) {
                    console.log("Got single KTA to fetch some accumulation examples", action);
                    if (!action.payload || (action.payload as unknown as Kta[]).length === 0) {
                        console.log("No KTA data found for the given filters");
                        return;
                    }
                    (action.payload as unknown as Kta[]).forEach((kta: Kta) => {
                        console.log(
                            "Fetching accumulations for employment",
                            kta.employmentId,
                          kta.startDate,
                          kta.endDate,
                        );

                        // TODO if no end date, use 18 months from start date .. but what should we use?
                        const endDate = resolveKtaQueryEndDate(kta);
                        console.log("resolved end date for accumulation fetch", endDate);
                        fetchAccumulations({
                          employmentId: kta.employmentId,
                          scope: "EMPLOYMENT",
                          stateNames: "keskiansiolaskenta",
                          from: kta.startDate,
                          to: endDate,
                            // ...(kta.endDate ? { to: kta.endDate }),
                        }).then((acc) => {
                            if (acc && acc.data) {
                                //for (const item of acc.data.reverse()) {
                                const dates = acc.data.map((item) => {
                                    const dates = item.rows.map((row) => {
                                        const ka = row.keskiansiolaskenta as AccumulationCalculationMultiple;
                                        const avgs = ka.ongoingAverages;
                                        let keys: string[] = [];
                                        if (avgs) {
                                            Array.from(Object.keys(avgs))
                                                .sort()
                                                .reverse()
                                                .forEach((key) => {
                                                    const avg = avgs[key];
                                                    console.log("Accumulation ongoing average", key, avg);
                                                    keys.push(key);
                                                });
                                        }
                                        return keys;
                                    });
                                    return Array.from(new Set(dates.flat()));
                                });
                                const allValues = getValues("ktaComponents");
                                const allKeskiansioValues = getValues("ktaKeskiansioComponents");
                                const uniqDates = Array.from(new Set(dates.flat())).sort();
                                console.log("uniq dates", uniqDates);
                                for (const date of uniqDates) {
                                    addExpands("ktaComponents" + date, false);
                                }
                                const nval = uniqDates.map((d) => {
                                    const currentValue = allValues.find(
                                        (value) => value.date === d,
                                    );
                                    return {
                                        date: d,
                                        increasePercentage: currentValue?.increasePercentage || "",
                                        changeSalary: currentValue?.changeSalary || "",
                                        increaseAmount: currentValue?.increaseAmount || "",
                                    };
                                });
                                setValue("ktaComponents", nval);

                                const nkaval = [salaryIncreaseStartDate].map((d) => {

                                    const dt = format(d, isoFormat)
                                    const currentKeskiansio = allKeskiansioValues.find(v => v.date === dt);
                                    addExpands("ktaKeskiansioComponents" + dt, true)
                                    return {
                                        date: getFormattedDate(d, false),
                                        increasePercentage: currentKeskiansio?.increasePercentage || "",
                                        changeSalary: currentKeskiansio?.changeSalary || "",
                                        increaseAmount: currentKeskiansio?.increaseAmount || "",
                                    };
                                });
                                setValue("ktaKeskiansioComponents", nkaval)
                            }
                        });
                    });
                }
            });
        } else {
            console.log("No TES or invalid date", tes, salaryIncreaseStartDate);
        }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tes]);

    const fa = useFieldArray({
        control,
        rules: {required: true},
        name: "ktaComponents",
    });

    const faKeskiansio = useFieldArray({
        control,
        rules: {required: true},
        name: "ktaKeskiansioComponents",
    });

    return (
        <>
            <SalaryIncreaseConfigBasic ktaMode={true}/>
            <KtaIncreaseConfigRows
                rows={faKeskiansio.fields}
                title="keskiansio "
                rowKey="ktaKeskiansioComponents"
                handleExpands={handleExpands}
                expands={expands}
            />
            <KtaIncreaseConfigRows
                rows={fa.fields}
                title="keskiansiolaskenta "
                rowKey="ktaComponents"
                handleExpands={handleExpands}
                expands={expands}
            />
        </>
    );
};

export default KtaIncreaseConfig;

